"use client";

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import { CHART_COLORS, MetricTier, METRIC_LABELS } from "@/lib/types";

interface LanguageBarChartProps {
  data: { language: string; [key: string]: number | string }[];
  metric: MetricTier;
  title: string;
  height?: number;
}

export default function LanguageBarChart({ data, metric, title, height = 320 }: LanguageBarChartProps) {
  const sorted = [...data].sort((a, b) => (a[metric] as number) - (b[metric] as number));
  const avg = sorted.reduce((s, d) => s + (d[metric] as number), 0) / sorted.length;

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide">{title}</h3>
        <span className="text-[10px] text-[var(--color-muted)]">avg: {avg.toFixed(2)}%</span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={sorted} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="language"
            tick={{ fontSize: 10 }}
            tickFormatter={(v: string) => v.slice(0, 3).toUpperCase()}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `${v}%`}
          />
          <Tooltip
            formatter={(value: unknown) => [`${Number(value).toFixed(2)}%`, METRIC_LABELS[metric]]}
            contentStyle={{
              background: "#18181b",
              border: "1px solid #3f3f46",
              borderRadius: "8px",
              fontSize: "12px",
            }}
          />
          <ReferenceLine y={avg} stroke="#71717a" strokeDasharray="5 5" />
          <Bar dataKey={metric} radius={[4, 4, 0, 0]} maxBarSize={40}>
            {sorted.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
